import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { UserInfo, UserModel } from '@models/authentication/auth';
import { MenuAccessPermissionTypes, Permission, PermissionTypes, RequiredPermissions } from '@models/authorization';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { IdentityService } from './identity.service';

type IdentityState = {
  isAuthenticated: boolean;
  isSystemAdmin: boolean;
  isLoading: boolean;
  user: UserModel;
};

const initialState: IdentityState = {
  isAuthenticated: false,
  isSystemAdmin: true,
  isLoading: false,
  user: {
    userinfo: {} as UserInfo,
    permissions: [],
    expires_at: 0,
  } as UserModel,
};

export const IdentityStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('identity-store'),
  withComputed(state => ({
    userId: computed(() => state.user()?.userinfo?.sub || null),
    tokenExpiration: computed(() => state.user()?.expires_at || 0),
    permissions: computed(() => state.user()?.permissions || []),
    permissionTypes: computed(() => {
      const permissions = state.user()?.permissions || [];
      return permissions.length ? permissions.map(perm => perm.name) : [];
    }),
  })),

  withMethods((store, identityService = inject(IdentityService)) => {
    const authMethods = {
      checkAuth: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isAuthenticated: false, isLoading: true })),
          switchMap(() =>
            identityService.checkAuth().pipe(
              tap(({ userinfo, permissions, expires_at }) => {
                patchState(store, {
                  user: { userinfo, permissions, expires_at },
                  isAuthenticated: true,
                  isLoading: false,
                  isSystemAdmin: true,
                });
              }),
              catchError(err => {
                patchState(store, {
                  isAuthenticated: err.status === 403,
                  isLoading: false,
                  isSystemAdmin: false,
                });

                if (err.status !== 403) return identityService.login();

                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      refresh: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(() =>
            identityService.refresh().pipe(
              tap(({ expires_at }) => {
                patchState(store, state => ({
                  user: { ...state.user, expires_at },
                  isAuthenticated: true,
                  isLoading: false,
                  isSystemAdmin: true,
                }));
              }),
              catchError(() => {
                return identityService.logout();
              }),
            ),
          ),
        ),
      ),

      login: rxMethod<void>(pipe(switchMap(() => identityService.login().pipe(catchError(() => EMPTY))))),
      logout: rxMethod<void>(pipe(switchMap(() => identityService.logout().pipe(catchError(() => EMPTY))))),
    };

    const permissionMethods = {
      hasAllPermissions: (permissionNames: PermissionTypes[]) => {
        const permissionTypes = store.permissionTypes();
        let hasAllPermissions = true;
        for (const name of permissionNames) {
          if (!permissionTypes.includes(name)) {
            hasAllPermissions = false;
            break;
          }
        }
        return hasAllPermissions;
      },

      hasAtLeastOnePermission: (permissionNames: PermissionTypes[]) => {
        const permissionTypes = store.permissionTypes();
        for (const name of permissionNames) {
          if (permissionTypes.includes(name)) {
            return true;
          }
        }
        return false;
      },

      hasPermissionForMenuAccess: (requiredPermissions: MenuAccessPermissionTypes[]) => {
        const permissionTypes = store.permissionTypes();
        return permissionTypes.some(userPermission => requiredPermissions.includes(userPermission as MenuAccessPermissionTypes));
      },

      hasPermissionToAccessPage: ({ oneAtLeast, menuAccess }: RequiredPermissions) => {
        const permissionTypes = store.permissionTypes();

        const hasAllMenuPermissions = !menuAccess?.length || menuAccess.every(permission => permissionTypes.includes(permission));

        const hasOtherAccess = !oneAtLeast?.length || permissionTypes.some(permType => oneAtLeast.includes(permType));

        return hasAllMenuPermissions && hasOtherAccess;
      },
    };

    // Reset state
    const resetState = () => patchState(store, initialState);

    return {
      ...authMethods,
      ...permissionMethods,
      resetState,
    };
  }),
);

export interface IdentityStoreType {
  // state signals
  readonly isAuthenticated: Signal<boolean>;
  readonly isSystemAdmin: Signal<boolean>;
  readonly isLoading: Signal<boolean>;
  readonly user: Signal<UserModel>;
  readonly userId: Signal<string | null>;
  readonly tokenExpiration: Signal<number>;
  readonly permissions: Signal<Permission[]>;
  readonly permissionTypes: Signal<string[]>;

  // auth methods
  readonly checkAuth: () => void;
  readonly refresh: () => void;
  readonly login: () => void;
  readonly logout: () => void;

  // permission methods
  readonly hasAllPermissions: (permissionNames: PermissionTypes[]) => boolean;
  readonly hasAtLeastOnePermission: (permissionNames: PermissionTypes[]) => boolean;
  readonly hasPermissionForMenuAccess: (requiredPermissions: MenuAccessPermissionTypes[]) => boolean;
  readonly hasPermissionToAccessPage: (requiredPermissions: RequiredPermissions) => boolean;
  readonly resetState: () => void;
}
